import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PencilIcon,
  ArrowPathIcon,
  PlayIcon,
  PauseIcon,
  EyeIcon,
  Squares2X2Icon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { openAIWithMessage } from '../aiAssistant/AIAssistantButton';
import { 
  CircularGauge, 
  LinearGauge, 
  TankGauge 
} from './widgets/GaugeWidgets';
import { UniversalWidget } from './widgets/UnifiedWidgetSystem';
import WidgetEditor from './WidgetEditor';
import { resumeWidgetEditorFromPicker } from '../../utils/signalkPickerReturn';
import DataSourceIndicator from './widgets/DataSourceIndicator';
import BilgeMonitorSection from './bilge/BilgeMonitorSection';
import { clampBilgeInsertIndex } from '../../utils/bilgeDashboardLayout';
import { normalizeSignalkScalar, resolveWidgetDataPath, pickWidgetSaveDataPath, flattenSignalkTree, formatWidgetDisplayValue, parseWidgetNumericValue, coerceWidgetDecimals, resolveWidgetConfig } from '../../utils/signalKValueFormat';
import { resolveWidgetValueColor } from '../../utils/dashboardStatusAlarmUtils';
import { useSignalkDashboardData } from '../../hooks/useSignalkDashboardData';
import { normalizeSections } from '../../utils/dashboardSectionUtils';
import { normalizeWidgets, widgetDisplayName } from '../../utils/dashboardWidgetUtils';
import { sectionGridColumnCount, widgetGridColumnSpan } from '../../utils/dashboardGridUtils';
import {
  INSTRUMENT,
  instrumentBarColor,
  toDisplayPercent,
  formatCapacitySubtitle,
} from './widgets/instrumentLook';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background.primary};
  padding: 2rem;
  max-width: 1024px;
  margin: 0 auto;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${theme.colors.background.tertiary};
  }
`;

const DashboardTitle = styled.h1`
  margin: 0;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.h2};
  font-weight: ${theme.fontWeights.bold};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${props => props.variant === 'primary' ? theme.colors.primary.main : theme.colors.background.secondary};
  color: ${props => props.variant === 'primary' ? 'white' : theme.colors.text.primary};
  border: 1px solid ${props => props.variant === 'primary' ? theme.colors.primary.main : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${props => props.variant === 'primary' ? theme.colors.primary.dark : theme.colors.background.tertiary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DashboardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #475569;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: ${INSTRUMENT.accent};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const WidgetsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$columns || 2}, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
  padding: 8px 0;
`;

const Widget = styled.div`
  background: ${INSTRUMENT.cardBg};
  border: 1px solid ${INSTRUMENT.cardBorder};
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 108px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  grid-column: span ${props => props.$span || 1};
  
  &:hover {
    border-color: ${INSTRUMENT.cardHoverBorder};
    box-shadow: 0 0 0 1px ${INSTRUMENT.cardHoverBorder}, inset 0 1px 0 rgba(255, 255, 255, 0.06);
  }
  
  &:hover .widget-hover-overlay {
    opacity: 1;
    pointer-events: auto;
  }
`;

const WidgetHoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.72);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 10;
  backdrop-filter: blur(2px);
`;

const WidgetOverlayButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const EditWidgetButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  transition: all 0.2s ease;
  
  &:hover {
    background: #2563eb;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }
`;

const AskAIButton = styled.button`
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%);
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
  }
`;

const WidgetTitle = styled.h3`
  margin: 0;
  color: ${INSTRUMENT.label};
  font-size: 11px;
  font-weight: 600;
  text-align: left;
  line-height: 1.3;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  flex: 1;
  min-width: 0;
`;

const WidgetTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 10px;
`;

const WidgetValue = styled.div`
  font-size: 1.85rem;
  font-weight: 700;
  color: ${props => props.$color || INSTRUMENT.value};
  text-align: left;
  line-height: 1.1;
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 6px;
  letter-spacing: -0.02em;
`;

const WidgetUnit = styled.div`
  font-size: 0.85rem;
  color: ${INSTRUMENT.muted};
  font-weight: 500;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  margin-top: 0.4rem;
  padding: 0.15rem 0.55rem;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${props => props.$color || INSTRUMENT.healthy};
  border: 1px solid ${props => props.$color || INSTRUMENT.healthy};
  background: transparent;
`;

const InstrumentRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
`;

const InstrumentSub = styled.div`
  margin-top: 0.35rem;
  font-size: 0.75rem;
  color: ${INSTRUMENT.muted};
  font-weight: 500;
`;

const BarTrack = styled.div`
  width: 100%;
  height: 14px;
  background: ${INSTRUMENT.track};
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.55rem;
  border: 1px solid ${INSTRUMENT.cardBorder};
`;

const BarFill = styled.div`
  height: 100%;
  width: ${props => props.$pct || 0}%;
  background: ${props => props.$color || INSTRUMENT.accent};
  border-radius: 3px;
  transition: width 0.35s ease, background 0.25s ease;
`;

/** Status / text values on dark instrument cards. */
const TextWidgetValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${INSTRUMENT.healthy};
  text-align: left;
  margin: 0.35rem 0;
  line-height: 1.35;
  word-break: break-word;
  text-transform: capitalize;
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  color: ${theme.colors.text.secondary};
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 50vh;
  color: ${theme.colors.error.main};
  text-align: center;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 50vh;
  color: ${theme.colors.text.secondary};
  text-align: center;
`;

const ViewDashboards = () => {
  
  const { id, token: shareToken } = useParams();
  const isPublicShare = Boolean(shareToken);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [editingWidget, setEditingWidget] = useState(null);
  const [sections, setSections] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const dashboardRef = useRef(null);
  const [showBackButton, setShowBackButton] = useState(false);

  const canEdit = !isPublicShare && dashboard?.can_edit === true;
  const isSharedView = isPublicShare || (dashboard && dashboard.is_owner === false);

  const { loadSignalKData, resolveWidgetValue, wsConnected, signalKData } = useSignalkDashboardData(
    dashboard?.vessel_id,
    { enableWebSocket: !isPublicShare, shareToken: shareToken || null }
  );

  const loadSignalKDataRef = useRef(loadSignalKData);
  const isAutoRefreshRef = useRef(isAutoRefresh);
  useEffect(() => {
    loadSignalKDataRef.current = loadSignalKData;
  }, [loadSignalKData]);
  useEffect(() => {
    isAutoRefreshRef.current = isAutoRefresh;
  }, [isAutoRefresh]);

  // Time-series buffer for line chart widgets
  const [chartData, setChartData] = useState({});

  useEffect(() => {
    if (!signalKData || Object.keys(signalKData).length === 0) return;
    const flat = flattenSignalkTree(signalKData);
    setChartData((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [path, entry] of Object.entries(flat)) {
        const num = parseFloat(entry.value);
        if (Number.isNaN(num)) continue;
        const series = next[path] ? [...next[path]] : [];
        const last = series[series.length - 1];
        if (last && last.value === num && Date.now() - last.timestamp < 2000) continue;
        series.push({ timestamp: Date.now(), value: num });
        if (series.length > 20) series.shift();
        next[path] = series;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [signalKData]);

  useEffect(() => {
    dashboardRef.current = dashboard;
  }, [dashboard]);

  useEffect(() => {
    // Check if user came from Manage Dashboards page
    const referrer = document.referrer;
    const fromManageDashboards = referrer.includes('/dashboards') && !referrer.includes('/dashboard/');
    setShowBackButton(!isPublicShare && fromManageDashboards);
    
    if (isPublicShare || id) {
      loadDashboard();
    } else {
      navigate('/dashboards');
    }
  }, [id, shareToken, isPublicShare]);

  // Keep DB snapshot refreshing without capturing a stale loadSignalKData closure.
  useEffect(() => {
    if (!isPublicShare && !id) return undefined;
    const interval = setInterval(() => {
      if (!isAutoRefreshRef.current) return;
      loadSignalKDataRef.current();
      setLastUpdate(new Date());
    }, 8000);
    setRefreshInterval(interval);
    return () => clearInterval(interval);
  }, [id, shareToken, isPublicShare]);

  useEffect(() => {
    if (isPublicShare || !id || !sections.length) return;
    const resumed = resumeWidgetEditorFromPicker(id, sections);
    if (!resumed) return;
    if (resumed.error) {
      toast.error(resumed.error);
      return;
    }
    setEditingWidget(resumed);
    toast.success('Path applied — review and save the widget');
  }, [id, sections, isPublicShare]);

  const startAutoRefresh = () => {
    /* interval owned by useEffect above */
  };

  const loadDashboard = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      let responseData;
      if (isPublicShare) {
        const apiUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port && window.location.port !== '80' && window.location.port !== '443' ? `:${window.location.port}` : ''}`;
        const response = await fetch(`${apiUrl}/api/dashboard-sharing/shared/${shareToken}`);
        if (!response.ok) {
          throw new Error('Shared dashboard not found or expired');
        }
        const payload = await response.json();
        responseData = payload.dashboard;
        setShareInfo(payload.share_info || null);
      } else {
        const response = await api.get(`/api/dashboards/${id}`);
        responseData = response.data;
        setShareInfo(null);
      }
      
      const normalized = normalizeSections(responseData.sections || []).map((section) => ({
        ...section,
        widgets: normalizeWidgets(section.widgets),
      }));
      setDashboard({ ...responseData, sections: normalized });
      setSections(normalized);
      if (responseData?.vessel_id) {
        loadSignalKData(responseData.vessel_id);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(isPublicShare ? (err.message || 'Failed to load shared dashboard') : 'Failed to load dashboard');
      if (!silent) {
        toast.error(isPublicShare ? 'This dashboard link is invalid or has expired' : 'Failed to load dashboard');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const getSignalKValue = (dataPath, widgetConfig = null) =>
    resolveWidgetValue(dataPath, widgetConfig || {});

  const getValueColor = (widget, value) => resolveWidgetValueColor(widget, value);

  const handleEdit = () => {
    navigate(`/dashboard/${id}/edit`);
  };

  const handleRefresh = () => {
    loadDashboard();
    loadSignalKData();
    setLastUpdate(new Date());
    toast.success('Dashboard refreshed');
  };

  const handleEditWidget = (widget, sectionId) => {
    setEditingWidget({ ...widget, sectionId });
  };

  const handleSaveWidget = async (updatedWidget) => {
    try {
      
      // Prepare widget data for backend
      // Use dataSource as the primary field (what user edited in the form)
      const dataPathValue = pickWidgetSaveDataPath(updatedWidget);
      
      if (!dataPathValue) {
        toast.error('Select a SignalK data path before saving');
        return;
      }
      
      const widgetData = {
        widget_name: updatedWidget.name,
        widget_type: updatedWidget.type,
        data_path: dataPathValue,
        dataPath: dataPathValue,
        data_source: 'signalk',
        width: updatedWidget.width,
        section_id: updatedWidget.sectionId,
        config: {
          ...updatedWidget.config,
          dataPath: dataPathValue,
        },
      };
      
      
      // Save to backend
      const response = await api.put(`/api/dashboards/${id}/widgets/${updatedWidget.id}`, widgetData);
      
      
      setEditingWidget(null);
      toast.success('Widget updated successfully');
      
      // Reload dashboard to get fresh data
      await loadDashboard(false); // Use false to show loading state
      
      // Also reload SignalK data to refresh widget values
      await loadSignalKData();
    } catch (error) {
      console.error('❌ Error saving widget:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to save widget: ' + (error.response?.data?.error || error.message));
    }
  };

  const toggleAutoRefresh = () => {
    setIsAutoRefresh(!isAutoRefresh);
    if (!isAutoRefresh) {
      startAutoRefresh();
    } else if (refreshInterval) {
      clearInterval(refreshInterval);
    }
  };

  const renderWidgetContent = (widget, signalData) => {
    const widgetConfig = resolveWidgetConfig(widget);
    const widgetType = widget.type || widget.widget_type || 'digital';
    const displayType =
      widgetConfig.displayType ||
      widgetType ||
      (widgetConfig.gaugeType ? 'gauge' : 'digital');
    const value = sanitizeValue(signalData.value);
    const unit = signalData.unit || widgetConfig.unit || '';
    const min = widgetConfig.min || 0;
    const max = widgetConfig.max || 100;

    switch (displayType) {
      case 'gauge':
        return renderGaugeWidget(widget, value, unit, min, max, signalData);
      
      case 'chart':
      case 'line':
        return renderChartWidget(widget.name, value, unit, widget, signalData);
      
      case 'bar':
        return renderBarWidget(widget.name, value, unit, min, max, widget);
      
      case 'status':
        return renderStatusWidget(widget.name, value, unit);
      
      case 'battery-monitor':
        return renderBatteryMonitorWidget(widget.name, value, unit, widget);
      
      case 'garmin-rpm':
      case 'garmin-speed':
      case 'garmin-temperature':
      case 'garmin-voltage':
      case 'garmin-circular':
        return renderGarminWidget(widget, value, unit);
      
      case 'text':
        return renderTextWidget(widget.name, value, unit, widget, signalData);
      
      case 'digital':
      default:
        return renderDigitalWidget(widget.name, value, unit, widget, signalData);
    }
  };

  const sanitizeValue = (val) => {
    const scalar = normalizeSignalkScalar(val);
    if (scalar === null || scalar === undefined) return '--';
    return scalar;
  };

  const renderDigitalWidget = (name, value, unit, widget = null, signalData = null) => {
    const widgetConfig = resolveWidgetConfig(widget);
    const decimals = coerceWidgetDecimals(widgetConfig, 1);

    let numeric = parseWidgetNumericValue(value);
    if (numeric !== null && unit === '%' && numeric >= 0 && numeric <= 1) {
      numeric = numeric * 100;
    }

    const valueColor = widget ? getValueColor(widget, numeric ?? value) : INSTRUMENT.value;
    const formatted =
      numeric !== null
        ? numeric.toFixed(decimals)
        : formatWidgetDisplayValue(value, widgetConfig, decimals);
    const displayUnit = !unit
      ? null
      : /^current$/i.test(String(unit).trim())
        ? 'A'
        : String(unit).trim();

    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <WidgetTitleRow>
          <WidgetTitle>{name}</WidgetTitle>
          {signalData && (
            <DataSourceIndicator sourceType={signalData.sourceType} lastUpdate={signalData.lastUpdate} />
          )}
        </WidgetTitleRow>
        <div style={{ marginTop: 'auto' }}>
          <WidgetValue $color={valueColor === '#3b82f6' ? INSTRUMENT.value : valueColor}>
            {formatted}
            {displayUnit ? <WidgetUnit as="span">{displayUnit}</WidgetUnit> : null}
          </WidgetValue>
        </div>
      </div>
    );
  };

  const renderGaugeWidget = (widget, value, unit, min, max, signalData) => {
    const gaugeType = resolveWidgetConfig(widget).gaugeType || 'circular';
    const config = resolveWidgetConfig(widget);
    
    switch (gaugeType) {
      case 'linear':
        return <LinearGauge name={widget.name} value={value} unit={unit} min={min} max={max} config={config} sourceType={signalData.sourceType} lastUpdate={signalData.lastUpdate} />;
      
      case 'tank':
        return <TankGauge name={widget.name} value={value} unit={unit} min={min} max={max} config={config} sourceType={signalData.sourceType} lastUpdate={signalData.lastUpdate} />;
      
      case 'circular':
      default:
        return <CircularGauge name={widget.name} value={value} unit={unit} min={min} max={max} config={config} sourceType={signalData.sourceType} lastUpdate={signalData.lastUpdate} />;
    }
  };

  const renderBarWidget = (name, value, unit, min, max, widget = null) => {
    const numValue = parseWidgetNumericValue(value);
    const preferred = widget ? getValueColor(widget, numValue ?? value) : null;
    const pct = toDisplayPercent(numValue ?? value, unit, min, max);
    const clamped = pct == null ? 0 : pct;
    const fill = instrumentBarColor(clamped, widget, preferred);
    const displayPct = pct == null ? '--' : `${Math.round(clamped)}%`;
    const capacityLine = formatCapacitySubtitle(pct, widget);
    const rawLabel = numValue != null && unit && unit !== '%'
      ? `${formatWidgetDisplayValue(value, widget?.config || {})} ${unit}`
      : null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <InstrumentRow>
          <WidgetTitle style={{ margin: 0 }}>{name}</WidgetTitle>
          <WidgetValue $color={fill} style={{ fontSize: '1.45rem' }}>
            {displayPct}
          </WidgetValue>
        </InstrumentRow>
        <BarTrack>
          <BarFill $pct={clamped} $color={fill} />
        </BarTrack>
        {(capacityLine || rawLabel) && (
          <InstrumentSub>{capacityLine || rawLabel}</InstrumentSub>
        )}
      </div>
    );
  };

  const renderStatusWidget = (name, value, unit) => {
    const isActive = value && value !== '0' && value !== 'false' && value !== 'off';
    const chipColor = isActive ? INSTRUMENT.healthy : INSTRUMENT.critical;
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <WidgetTitleRow>
          <WidgetTitle>{name}</WidgetTitle>
        </WidgetTitleRow>
        <div style={{ marginTop: 'auto' }}>
          <WidgetValue $color={INSTRUMENT.value} style={{ fontSize: '1.35rem' }}>
            {isActive ? 'Active' : 'Inactive'}
          </WidgetValue>
          <StatusChip $color={chipColor}>{isActive ? 'ON' : 'OFF'}</StatusChip>
        </div>
      </div>
    );
  };

  const renderBatteryMonitorWidget = (name, value, unit, widget = null) => {
    const numValue = parseWidgetNumericValue(value) ?? 0;
    const isLow = numValue < 12.0;
    const isCritical = numValue < 11.5;
    const statusColor = isCritical ? INSTRUMENT.critical : isLow ? INSTRUMENT.caution : INSTRUMENT.healthy;
    const formatted = formatWidgetDisplayValue(value, widget?.config || {}, 1);
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <WidgetTitleRow>
          <WidgetTitle>{name}</WidgetTitle>
        </WidgetTitleRow>
        <div style={{ marginTop: 'auto' }}>
          <WidgetValue $color={INSTRUMENT.value}>
            {formatted}
            {unit ? <WidgetUnit as="span">{unit}</WidgetUnit> : null}
          </WidgetValue>
          <StatusChip $color={statusColor}>
            {isCritical ? 'Critical' : isLow ? 'Low' : 'Good'}
          </StatusChip>
        </div>
      </div>
    );
  };

  const renderGarminWidget = (widget, value, unit) => {
    const numValue = parseWidgetNumericValue(value) ?? 0;
    const widgetConfig = resolveWidgetConfig(widget);
    const config = {
      ...widgetConfig,
      precision: coerceWidgetDecimals(widgetConfig),
    };

    return (
      <div style={{ height: '100%', width: '100%' }}>
        <UniversalWidget
          type={widget.type}
          config={config}
          data={{ value: numValue }}
          size="medium"
        />
      </div>
    );
  };

  const renderTextWidget = (name, value, unit, widget = null, signalData = null) => {
    const display = formatWidgetDisplayValue(value, resolveWidgetConfig(widget), 1);
    return (
      <div>
        <WidgetTitleRow>
          <WidgetTitle>{name}</WidgetTitle>
          {signalData && (
            <DataSourceIndicator
              sourceType={signalData.sourceType}
              lastUpdate={signalData.lastUpdate}
            />
          )}
        </WidgetTitleRow>
        <TextWidgetValue>
          {display}
          {unit && display !== '--' ? ` ${unit}` : ''}
        </TextWidgetValue>
      </div>
    );
  };

  const renderChartWidget = (name, value, unit, widget, signalData) => {
    const dataPath = resolveWidgetDataPath(widget);
    const historicalData = chartData[dataPath] || [];
    const chartLabel = widget?.config?.chartLabel || name;
    
    // Calculate min/max for scaling
    const values = historicalData.map(d => d.value);
    const minValue = values.length > 0 ? Math.min(...values) : 0;
    const maxValue = values.length > 0 ? Math.max(...values) : 100;
    const valueRange = maxValue - minValue || 1;
    
    // Generate SVG points
    const points = historicalData.map((point, index) => {
      const x = 10 + (index / Math.max(historicalData.length - 1, 1)) * 180;
      const y = 50 - ((point.value - minValue) / valueRange) * 40;
      return `${x},${y}`;
    }).join(' ');
    
    return (
      <div>
        <WidgetTitleRow>
          <WidgetTitle>{name}</WidgetTitle>
          <DataSourceIndicator sourceType={signalData.sourceType} lastUpdate={signalData.lastUpdate} />
        </WidgetTitleRow>
        <div style={{ margin: '0.5rem 0' }}>
          <svg width="100%" height="60" viewBox="0 0 200 60" style={{ overflow: 'visible' }}>
            {/* Grid lines */}
            <line x1="10" y1="10" x2="190" y2="10" stroke="#334155" strokeWidth="0.5" strokeDasharray="2,2" />
            <line x1="10" y1="30" x2="190" y2="30" stroke="#334155" strokeWidth="0.5" strokeDasharray="2,2" />
            <line x1="10" y1="50" x2="190" y2="50" stroke="#334155" strokeWidth="0.5" strokeDasharray="2,2" />
            
            {/* Axes */}
            <line x1="10" y1="10" x2="10" y2="50" stroke="#64748b" strokeWidth="1" />
            <line x1="10" y1="50" x2="190" y2="50" stroke="#64748b" strokeWidth="1" />
            
            {/* Line chart */}
            {historicalData.length > 1 && (
              <>
                <polyline
                  points={points}
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="2"
                />
                {/* Last data point */}
                {historicalData.length > 0 && (
                  <circle 
                    cx={10 + ((historicalData.length - 1) / Math.max(historicalData.length - 1, 1)) * 180}
                    cy={50 - ((historicalData[historicalData.length - 1].value - minValue) / valueRange) * 40}
                    r="3" 
                    fill="#3b82f6" 
                  />
                )}
              </>
            )}
            
            {/* No data message */}
            {historicalData.length === 0 && (
              <text x="100" y="30" textAnchor="middle" fill="#94a3b8" fontSize="12">
                Collecting data...
              </text>
            )}
            
            {/* Min/Max labels */}
            {historicalData.length > 1 && (
              <>
                <text x="5" y="13" textAnchor="end" fill="#64748b" fontSize="9">{maxValue.toFixed(1)}</text>
                <text x="5" y="52" textAnchor="end" fill="#64748b" fontSize="9">{minValue.toFixed(1)}</text>
              </>
            )}
          </svg>
          
          {/* Legend */}
          {chartLabel && historicalData.length > 0 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.5rem',
              fontSize: '11px',
              color: '#64748b'
            }}>
              <div style={{ 
                width: '16px', 
                height: '2px', 
                backgroundColor: '#3b82f6',
                borderRadius: '1px'
              }}></div>
              <span>{chartLabel}</span>
              {unit && <span style={{ color: '#94a3b8' }}>({unit})</span>}
            </div>
          )}
          
          <div style={{ textAlign: 'center', marginTop: '0.25rem' }}>
            <WidgetValue style={{ fontSize: '1rem' }}>
              {formatWidgetDisplayValue(value, widget?.config || {})}
            </WidgetValue>
            {unit && <WidgetUnit>{unit}</WidgetUnit>}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingState>
          <div>Loading dashboard...</div>
        </LoadingState>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <ErrorState>
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <Button onClick={() => loadDashboard()}>Try Again</Button>
        </ErrorState>
      </DashboardContainer>
    );
  }

  if (!dashboard) {
    return (
      <DashboardContainer>
        <EmptyState>
          <h3>Dashboard Not Found</h3>
          <p>
            {isPublicShare
              ? 'This share link is invalid or has expired.'
              : "The dashboard you're looking for doesn't exist or you don't have access to it."}
          </p>
          {!isPublicShare && (
            <Button onClick={() => navigate('/dashboards')}>Back to Dashboards</Button>
          )}
        </EmptyState>
      </DashboardContainer>
    );
  }

  return (
    <>
    <DashboardContainer>
      <DashboardHeader>
        <HeaderLeft>
          {showBackButton && (
            <BackButton onClick={() => navigate('/dashboards')}>
              <ArrowLeftIcon className="w-4 h-4" />
              Back
            </BackButton>
          )}
          <DashboardTitle>
            {dashboard.name || dashboard.dashboard_name}
            {isSharedView && (
              <span style={{
                marginLeft: '0.75rem',
                fontSize: theme.fontSizes.sm,
                fontWeight: theme.fontWeights.medium,
                color: theme.colors.text.secondary,
              }}>
                {isPublicShare
                  ? (shareInfo?.owner_name ? `(shared by ${shareInfo.owner_name})` : '(shared view)')
                  : '(shared view)'}
              </span>
            )}
          </DashboardTitle>
        </HeaderLeft>
        
        <HeaderActions>
          {!isPublicShare && (
            <Button onClick={() => navigate('/dashboards')}>
              <Squares2X2Icon className="w-4 h-4" />
              Manage Dashboards
            </Button>
          )}
          
          <Button onClick={toggleAutoRefresh}>
            {isAutoRefresh ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
            {isAutoRefresh ? 'Pause' : 'Resume'} Auto-refresh
          </Button>
          <Button onClick={handleRefresh}>
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </Button>
          {canEdit && (
            <Button onClick={handleEdit}>
              <PencilIcon className="w-4 h-4" />
              Edit
            </Button>
          )}
        </HeaderActions>
      </DashboardHeader>

      <DashboardContent>
        {(() => {
          let tc = dashboard.theme_config;
          if (typeof tc === 'string') {
            try {
              tc = JSON.parse(tc);
            } catch {
              tc = {};
            }
          }
          const bilge = tc?.bilgeMonitor;
          const showBilge = !isPublicShare && !!(bilge?.enabled && bilge?.pumps?.length > 0);
          const orderedSections = [...sections].sort(
            (a, b) => (a.section_order ?? 0) - (b.section_order ?? 0)
          );
          const insertIndex = clampBilgeInsertIndex(bilge?.insertIndex, orderedSections.length);
          const bilgeEl = showBilge ? (
            <BilgeMonitorSection key="bilge-monitor" dashboardId={Number(id)} config={bilge} />
          ) : null;

          const renderSectionBlock = (section) => {
            const cols = sectionGridColumnCount(section);
            return (
            <Section key={section.id}>
              <SectionHeader>
                <SectionTitle>{section.name || section.section_name}</SectionTitle>
              </SectionHeader>

              {section.widgets && section.widgets.length > 0 ? (
                <WidgetsGrid $columns={cols}>
                  {section.widgets.map((widget) => {
                    const widgetConfig = resolveWidgetConfig(widget);
                    const signalData = getSignalKValue(resolveWidgetDataPath(widget), widgetConfig);

                    const widgetHeight = widget.config && widget.config.heightPx ? widget.config.heightPx : undefined;
                    const span = widgetGridColumnSpan(widget, cols);

                    return (
                      <Widget
                        key={widget.id}
                        $span={span}
                        style={widgetHeight ? { minHeight: widgetHeight } : undefined}
                      >
                        {renderWidgetContent(widget, signalData)}
                        {!isPublicShare && (
                        <WidgetHoverOverlay className="widget-hover-overlay">
                          <WidgetOverlayButtons>
                            <AskAIButton
                              onClick={() =>
                                openAIWithMessage(
                                  `What does my ${widget.name || widget.widget_name || 'this'} widget show? Is this reading normal?`
                                )
                              }
                            >
                              <SparklesIcon className="w-4 h-4" />
                              Ask AI
                            </AskAIButton>
                            {canEdit && (
                              <EditWidgetButton onClick={() => handleEditWidget(widget, section.id)}>
                                <PencilIcon className="w-4 h-4" />
                                Edit Widget
                              </EditWidgetButton>
                            )}
                          </WidgetOverlayButtons>
                        </WidgetHoverOverlay>
                        )}
                      </Widget>
                    );
                  })}
                </WidgetsGrid>
              ) : (
                <div style={{ textAlign: 'center', color: theme.colors.text.secondary, padding: '2rem' }}>
                  No widgets in this section
                </div>
              )}
            </Section>
          );
          };

          if (orderedSections.length === 0) {
            if (bilgeEl) return bilgeEl;
            return (
              <EmptyState>
                <EyeIcon className="w-16 h-16 mb-4 opacity-50" />
                <h3>Empty Dashboard</h3>
                <p>This dashboard doesn't have any sections or widgets yet.</p>
                {canEdit && (
                  <Button onClick={handleEdit} variant="primary">
                    <PencilIcon className="w-4 h-4" />
                    Edit Dashboard
                  </Button>
                )}
              </EmptyState>
            );
          }

          const blocks = orderedSections.flatMap((section, i) => {
            const row = [];
            if (bilgeEl && insertIndex === i) row.push(bilgeEl);
            row.push(renderSectionBlock(section));
            return row;
          });
          if (bilgeEl && insertIndex >= orderedSections.length) blocks.push(bilgeEl);

          return <>{blocks}</>;
        })()}
      </DashboardContent>
      
      {lastUpdate && (
        <div style={{ 
          position: 'fixed', 
          bottom: '1rem', 
          right: '1rem', 
          background: theme.colors.background.secondary,
          padding: '0.5rem 1rem',
          borderRadius: theme.borderRadius.md,
          fontSize: theme.fontSizes.xs,
          color: theme.colors.text.secondary,
          border: `1px solid ${theme.colors.border.primary}`
        }}>
          Last updated: {lastUpdate.toLocaleTimeString()}
        </div>
      )}
    </DashboardContainer>
    
    {editingWidget && !isPublicShare && (
      <WidgetEditor
        widget={editingWidget}
        sections={sections}
        onSave={handleSaveWidget}
        onClose={() => setEditingWidget(null)}
        vesselId={dashboard?.vessel_id}
        defaultSectionId={editingWidget.sectionId}
        dashboardId={id}
      />
    )}
    </>
  );
};

export default ViewDashboards;